import React, { FormEvent, useCallback, useEffect, useState } from "react";
import GenericTable from "components/table";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ACLEntry, ACLEntryPermission, ACLPayload, Role } from "types";
import { RoleRowAction } from "./RoleRowAction";
import { ChevronDown } from "lucide-react";
import { Dialog } from "components/dialog";
import { useConfirmation } from "components/dialog/confirmation";
import api, { fetchRoles } from "api/axios";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { useACL } from "layouts/agent/agent-info/agent-acl/_context/AgentACLContext";
import ACLSelect from "../../../components/ACLSelect";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<ACLEntry>();

function SearchTableRolesACL(props: { tableData: ACLEntry[] }) {
  const { t } = useTranslation();
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);
  const { agentInfo } = useAgentInfo();
  const { confirm } = useConfirmation();
  const { permissions, refetch } = useACL();
  const [addAclEntries, setAddAclEntries] = useState<ACLEntryPermission[]>(
    permissions.map((perm) => ({ permission: perm, access: "NOT_SET" }))
  );

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add state for roles, loading, error, pagination
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesTotal, setRolesTotal] = useState(0);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesSkip, setRolesSkip] = useState(0);
  const ROLES_TAKE = 20;

  // Fetch roles
  const fetchRolesList = useCallback(
    async (reset = false) => {
      setRolesLoading(true);
      setRolesError(null);
      try {
        const data = await fetchRoles({
          take: ROLES_TAKE,
          skip: reset ? 0 : rolesSkip,
        });
        setRolesTotal(data.total);
        setRoles((prev) => (reset ? data.items : [...prev, ...data.items]));
        if (reset) setRolesSkip(ROLES_TAKE);
        else setRolesSkip((prev) => prev + ROLES_TAKE);
      } catch (err) {
        setRolesError("Failed to fetch roles");
        handleAxiosAndCaughtError(err);
      } finally {
        setRolesLoading(false);
      }
    },
    [rolesSkip]
  );

  // Initial fetch
  useEffect(() => {
    if (isAddDialogOpen) fetchRolesList(true);
  }, [isAddDialogOpen, fetchRolesList]);

  const handleDeleteRow = async (id: string) => {
    const confirmed = await confirm({
      title: t("acl.deleteRole"),
      message: t("acl.deleteRoleMessage"),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
    });

    if (confirmed) {
      setDeletingRowId(id);
      try {
        await api.delete(`/agents/${agentInfo.id}/acl/${id}`);
        await refetch();
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setDeletingRowId(null);
      }
    }
  };

  const handleAddRole = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedRoleId) return;
    setIsSubmitting(true);
    try {
      const payload: ACLPayload = {
        subject_type: "ROLE",
        subject_id: selectedRoleId,
        subject_name: selectedRole.name,
        entries: addAclEntries,
      };
      await api.put(`/agents/${agentInfo.id}/acl`, payload);
      await refetch();
      setIsSubmitting(false);
      setIsAddDialogOpen(false);
      setSelectedRoleId("");
      setIsDropdownOpen(false);
      setAddAclEntries(
        permissions.map((perm) => ({ permission: perm, access: "NOT_SET" }))
      );
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const columns: ColumnDef<ACLEntry, any>[] = [
    columnHelper.accessor("subject_name", {
      id: "name",
      header: () => <p>{t("acl.roleName")}</p>,
      cell: (info: any) => (
        <div className="flex w-full items-center gap-[14px]">
          <p className="font-medium text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <p>{t("common.description")}</p>,
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()?.length > 0
            ? info.getValue()
            : t("library.noDescriptionProvided")}
        </p>
      ),
    }),
    columnHelper.accessor("subject_id", {
      id: "actions",
      header: () => (
        <p className="p-4 text-left font-medium text-gray-900 dark:text-gray-100"></p>
      ),
      cell: (info) => (
        <RoleRowAction
          info={info}
          deletingRowId={deletingRowId}
          handleDeleteRow={handleDeleteRow}
        />
      ),
    }),
  ];

  // Inject deletingRowId and handleDeleteRow into each row for the action cell
  const dataWithActions = props.tableData.map((row) => ({
    ...row,
    deletingRowId,
    handleDeleteRow,
  }));

  // Optionally, highlight the row being deleted
  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return deletingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  const selectedRole = roles.find((role) => role.id === selectedRoleId);

  return (
    <>
      <GenericTable<ACLEntry>
        data={dataWithActions}
        columns={columns}
        renderRowClassName={renderRowClassName}
        searchPlaceholder={t("acl.searchRoles")}
        onAdd={() => setIsAddDialogOpen(true)}
        debug={true}
        addLabel={t("acl.addRole")}
      />
      <Dialog
        header={t("acl.addRole")}
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
      >
        <form
          onSubmit={handleAddRole}
          className="mx-auto flex min-w-max max-w-md flex-col gap-6  sm:max-w-lg md:max-w-xl lg:max-w-2xl"
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("acl.selectRole")}
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:bg-gray-50 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 sm:px-4"
              >
                {selectedRole ? (
                  <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-8 sm:w-8 sm:text-sm">
                      {selectedRole.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white sm:text-base">
                        {selectedRole.name}
                      </p>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                    {t("acl.chooseRole")}
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`ml-2 flex-shrink-0 text-gray-400 transition-transform sm:h-5 sm:w-5 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                  <div className="max-h-48 overflow-y-auto sm:max-h-60">
                    {rolesLoading && roles.length === 0 && (
                      <div className="flex items-center justify-center p-3 text-sm text-gray-500 sm:p-4">
                        {t("common.loading")}
                      </div>
                    )}
                    {rolesError && roles.length === 0 && (
                      <div className="flex flex-col items-center p-3 sm:p-4">
                        <span className="text-sm text-red-500">
                          {rolesError}
                        </span>
                        <button
                          type="button"
                          className="mt-2 text-sm text-blue-500 underline"
                          onClick={() => fetchRolesList(true)}
                        >
                          {t("common.retry")}
                        </button>
                      </div>
                    )}
                    {roles.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => {
                          setSelectedRoleId(role.id);
                          setIsDropdownOpen(false);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700 sm:gap-3 sm:px-4 sm:py-3"
                      >
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-bold text-blue-700 sm:h-10 sm:w-10 sm:text-sm">
                          {role.name.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-white sm:text-base">
                            {role.name}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {role.description}
                          </p>
                        </div>
                      </button>
                    ))}
                    {rolesError && roles.length > 0 && (
                      <div className="flex flex-col items-center p-2">
                        <span className="text-xs text-red-500">
                          {rolesError}
                        </span>
                        <button
                          type="button"
                          className="mt-1 text-xs text-blue-500 underline"
                          onClick={() => fetchRolesList(false)}
                        >
                          {t("common.retry")}
                        </button>
                      </div>
                    )}
                    {!rolesLoading &&
                      roles.length < rolesTotal &&
                      !rolesError && (
                        <button
                          type="button"
                          className="w-full p-2 text-center text-sm text-blue-500 hover:underline"
                          onClick={() => fetchRolesList(false)}
                        >
                          {t("acl.loadMore")}
                        </button>
                      )}
                    {rolesLoading && roles.length > 0 && (
                      <div className="flex items-center justify-center p-2 text-xs text-gray-400">
                        {t("acl.loadingMore")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("acl.setPermissions")}
            </label>
            <ACLSelect
              permissions={permissions}
              value={addAclEntries}
              onChange={setAddAclEntries}
              loading={isSubmitting}
            />
          </div>

          <div className="flex flex-col gap-3 pt-6 sm:flex-row sm:gap-4">
            <button
              type="button"
              onClick={() => {
                setIsAddDialogOpen(false);
                setSelectedRoleId("");
                setIsDropdownOpen(false);
              }}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-20 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting || !selectedRoleId}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-1.5">
                  <div className="border-t-transparent h-3 w-3 animate-spin rounded-full border-2 border-white"></div>
                  <span className="text-sm">{t("acl.adding")}</span>
                </div>
              ) : (
                t("acl.addRole")
              )}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

export default SearchTableRolesACL;
